import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { PublicLayout } from "../components/PanelLayout";
import { Alert, Button, Card, TextInput } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { homeFor, type Me, useMe } from "../lib/auth";

type Step = "email" | "code" | "totp";

/** Login da equipe: código por e-mail e, quando configurado, o aplicativo autenticador. */
export function Login() {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [totp, setTotp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const { data: me } = useMe();

  useEffect(() => {
    if (me)
      navigate(
        me.totpRequired && !me.totpConfirmed
          ? "/entrar/autenticador"
          : (params.get("voltar") ?? homeFor(me)),
        { replace: true },
      );
  }, [me, navigate, params]);

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/v1/auth/code", { email });
      setStep("code");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, string> = { email, code };
      if (step === "totp") {
        if (useRecovery) body.recoveryCode = totp;
        else body.totp = totp;
      }
      const r = await api.post<{ status: "ok" | "totp_required" }>("/v1/auth/verify", body);
      if (r.status === "totp_required") {
        setStep("totp");
        return;
      }
      const fresh = await api.get<Me>("/v1/me");
      qc.setQueryData(["me"], fresh);
      navigate(
        fresh.totpRequired && !fresh.totpConfirmed
          ? "/entrar/autenticador"
          : (params.get("voltar") ?? homeFor(fresh)),
        { replace: true },
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <Card>
        <h1>Entrar</h1>
        <div className="brand-stripe" aria-hidden>
          <span />
          <span />
          <span />
          <span />
        </div>
        <p className="muted" style={{ margin: "12px 0 16px" }}>
          Acesso da equipe ByFluent: vendedoras, professores e administração.
        </p>
        {error ? <Alert kind="risk">{error}</Alert> : null}
        {step === "email" ? (
          <form className="stack" onSubmit={requestCode} style={{ marginTop: 12 }}>
            <TextInput
              label="E-mail"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" block loading={busy}>
              Receber código
            </Button>
          </form>
        ) : (
          <form className="stack" onSubmit={verify} style={{ marginTop: 12 }}>
            <Alert kind="note">
              Se o e-mail estiver cadastrado, enviamos um código de 6 dígitos para {email}. Ele vale
              por 10 minutos.
            </Alert>
            <TextInput
              label="Código do e-mail"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            {step === "totp" ? (
              <>
                <TextInput
                  label={
                    useRecovery ? "Código de recuperação" : "Código do aplicativo autenticador"
                  }
                  inputMode={useRecovery ? "text" : "numeric"}
                  required
                  value={totp}
                  onChange={(e) =>
                    setTotp(
                      useRecovery
                        ? e.target.value.toUpperCase()
                        : e.target.value.replace(/\D/g, ""),
                    )
                  }
                />
                <Button variant="ghost" size="small" onClick={() => setUseRecovery(!useRecovery)}>
                  {useRecovery ? "Usar o aplicativo autenticador" : "Usar um código de recuperação"}
                </Button>
              </>
            ) : null}
            <Button type="submit" block loading={busy}>
              Entrar
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setStep("email");
                setCode("");
                setTotp("");
              }}
            >
              Usar outro e-mail
            </Button>
          </form>
        )}
      </Card>
    </PublicLayout>
  );
}
